import api from './api';

export interface DateOverrideResponse {
  overrideDate: string | null;
  isActive: boolean;
}

export interface SetDateOverrideRequest {
  overrideDate?: string | null;
}

export interface Shelter {
  id: string;
  name: string;
  sold_to: string;
  join_program_date: string;
  created_at: string;
  updated_at: string;
}

export interface UpdateJoinDateRequest {
  joinDate: string | null;
}

export interface JoinDateOverrideResponse {
  overrideDate: string | null;
  isActive: boolean;
}

export interface ClearReportsResponse {
  petParentReports: number;
  adoptionReports: number;
  adoptions: number;
  orphanPetParents: number;
  petParentAggregatedData: number;
}

export interface ExportFirestoreBackupResponse {
  success: boolean;
  timestamp: string;
  data: Record<string, unknown>;
  instructions: string;
}

let cachedDateOverride: DateOverrideResponse | null = null;
let hasDateOverrideCache = false;
let pendingDateOverrideRequest: Promise<DateOverrideResponse> | null = null;

export class DebugService {
  /**
   * Clear all reports and related data for the current shelter
   * Returns detailed counts of deleted records
   */
  static async clearReports(): Promise<ClearReportsResponse> {
    try {
      const response = await api.delete<ClearReportsResponse>('/debug/clear-reports');
      return response.data;
    } catch (error) {
      console.error('Failed to clear reports:', error);
      throw error;
    }
  }

  /**
   * Get current date override
   */
  static async getDateOverride(forceRefresh = false): Promise<DateOverrideResponse> {
    if (!forceRefresh) {
      if (hasDateOverrideCache && cachedDateOverride) {
        return cachedDateOverride;
      }

      if (pendingDateOverrideRequest) {
        return pendingDateOverrideRequest;
      }
    }

    try {
      pendingDateOverrideRequest = api
        .get<DateOverrideResponse>('/debug/date-override')
        .then((response) => {
          cachedDateOverride = response.data;
          hasDateOverrideCache = true;
          return response.data;
        });

      return await pendingDateOverrideRequest;
    } catch (error) {
      console.error('Failed to get date override:', error);
      throw error;
    } finally {
      pendingDateOverrideRequest = null;
    }
  }

  /**
   * Set date override for testing
   */
  static async setDateOverride(date: Date | null): Promise<DateOverrideResponse> {
    try {
      let dateOnly;
      if (date) {
        // Intentionally use LOCAL getters (getFullYear, getMonth, getDate) with Date.UTC()
        // to preserve the calendar date when converting to UTC.
        // The date picker provides a Date at midnight in local time,
        // and we want to send the same calendar date as UTC midnight to the backend.
        dateOnly = new Date(
          Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
        );
      }

      const payload: SetDateOverrideRequest = {
        overrideDate: dateOnly?.toISOString() ?? null,
      };

      const response = await api.post<DateOverrideResponse>('/debug/date-override', payload);
      cachedDateOverride = response.data;
      hasDateOverrideCache = true;
      return response.data;
    } catch (error) {
      console.error('Failed to set date override:', error);
      throw error;
    }
  }

  /**
   * Get current join date override
   */
  static async getJoinDateOverride(): Promise<JoinDateOverrideResponse> {
    try {
      const response = await api.get<JoinDateOverrideResponse>('/debug/join-date-override');
      return response.data;
    } catch (error) {
      console.error('Failed to get join date override:', error);
      throw error;
    }
  }

  /**
   * Set join date override for testing
   * This is temporary and does not modify the database
   * Pass null to clear the override and use shelter join date
   */
  static async setJoinDateOverride(date: Date | null): Promise<JoinDateOverrideResponse> {
    try {
      let dateOnly;
      if (date) {
        // Intentionally use LOCAL getters (getFullYear, getMonth, getDate) with Date.UTC()
        // to preserve the calendar date when converting to UTC.
        // The date picker provides a Date at midnight in local time,
        // and we want to send the same calendar date as UTC midnight to the backend.
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();
        dateOnly = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
      }

      const payload: UpdateJoinDateRequest = {
        joinDate: dateOnly?.toISOString() ?? null,
      };

      const response = await api.post<JoinDateOverrideResponse>(
        '/debug/join-date-override',
        payload
      );
      return response.data;
    } catch (error) {
      console.error('Failed to set join date override:', error);
      throw error;
    }
  }

  /**
   * Export Firestore database backup
   * Returns information about the backup and instructions for importing to local emulator
   */
  static async exportFirestoreBackup(): Promise<ExportFirestoreBackupResponse> {
    try {
      const response = await api.post<ExportFirestoreBackupResponse>(
        '/debug/export-firestore-backup'
      );
      return response.data;
    } catch (error) {
      console.error('Failed to export Firestore backup:', error);
      throw error;
    }
  }

  static async testSentryBackend(): Promise<{ eventId: string; timestamp: string }> {
    const response = await api.post<{ eventId: string; timestamp: string }>('/debug/sentry-test');
    return response.data;
  }
}
